<i18n src="@/locales/modules/ReportFormTranslate.json"></i18n>

<template>
    <report-form-config
        v-if="isShowCreateReportForm"
        v-model="reportFormData"
        :isCreateMode="true"
        @onSubmit="handleCreateReportForm"
        @onClickBack="handleClickBackCreateReportForm"
		@onIsFormDirtyChange="val => isCreateReportFormDirty = val"
    />
   <div v-else id="report-form-list" class="data-list-container">
		<div class="dropdown-button-container flex justify-end mt-16 md:hidden lg:hidden xl:hidden">
			<vs-input @input="mixinHandleSearch" size="large" class="input-search mr-3" icon="search" :placeholder="$t('Actions.Search')" v-model="searchText" />

			<vs-dropdown vs-custom-content vs-trigger-click v-if="mixinIsCanCreateReportForm">
				<vs-button class="btn-drop" type="filled" icon="expand_more"></vs-button>
				<vs-dropdown-menu>
					<vs-dropdown-item>
						<vs-button class="w-48 font-bold" type="filled" @click="handleClickAddNew">{{ $t("Actions.AddNew") }}</vs-button>
					</vs-dropdown-item>
				</vs-dropdown-menu>
			</vs-dropdown>
		</div>

		<vs-table
			ref="table"
			class="stack-table"
			:sst="true"
			:data="reportForms"
			:max-items="limitPerPage"
			:noDataText="$t('Notify.NoDataAvailable')"
			@sort="mixinHandleSort"
		>
			<div slot="header" class="flex flex-grow justify-end">
				<vs-input
					@input="mixinHandleSearch"
					size="large"
					class="input-search mr-3 md:block lg:block xl:block hidden"
					icon="search"
					:placeholder="$t('Actions.Search')"
					v-model="searchText"
				/>
				<div class="md:flex lg:flex xl:flex hidden" v-if="mixinIsCanCreateReportForm">
					<vs-button class="w-30 font-bold" type="filled" @click="handleClickAddNew">{{ $t("Actions.AddNew") }}</vs-button>
				</div>
			</div>

			<template slot="thead">
				<vs-th sort-key="name">{{ $t("ReportName") }}</vs-th>
				<vs-th>{{ $t("Status") }}</vs-th>
				<vs-th>{{ $t("Creator") }}</vs-th>
				<vs-th>{{ $t("UpdatedDate") }}</vs-th>
				<vs-th class="no-sorter"></vs-th>
			</template>

			<template slot-scope="{ data }">
				<tbody>
					<vs-tr :data="tr" :key="index" v-for="(tr, index) in data">
						<vs-td :data-header="$t('ReportName')">
							<p class="font-medium">{{ tr.name }}</p>
						</vs-td>

						<vs-td :data-header="$t('Status')">
							<p 
                                :class="[
                                    'font-medium font-bold max-w-xs', 
                                    { 'text-solid-green-1': tr.enable, 'text-solid-red': !tr.enable }
                                ]"
                            >{{ $t($options.filters.filterStatus(tr.enable)) }}</p>
						</vs-td>
						<vs-td :data-header="$t('Creator')">
							<p class="font-medium max-w-xs" v-if="tr.employee">{{ tr.employee.name }}</p>
						</vs-td>

						<vs-td :data-header="$t('UpdatedDate')">
							<p class="font-medium">{{ tr.updated_at | dateTime(userDateTimeFormat) }}</p>
						</vs-td>

						<vs-td class="whitespace-no-wrap text-right">
                            <report-form-context-menu
								type="vertical"
                                :reportForm="tr"
								@onClickDelete="showDeleteConfirm"
								@onClickEdit="id => handleNavigateToConfigPage(id)"
                            />
						</vs-td>
					</vs-tr>
				</tbody>
			</template>
		</vs-table>

		<CbsPagination class="mt-3" :total="totalPages" v-model="currentPage" />
	</div>
</template>

<script>
import _ from "lodash";
import {
	showConfirm,
	detectLeavePage,
	withBasicQueries,
	getUserPermissions,
	reportFormPermissions,
} from "@/mixins/mixins.js";
import { mapState } from "vuex";
import moduleReportForm from "@/store/report-form/moduleReportForm";

import ReportFormConfig from "./ReportFormConfig";
import ReportFormContextMenu from "./ReportFormContextMenu";

const initReportFormData = {
	code: "",
	name: "",
	isDisabled: false,
	category_id: null
};

export default {
    name: "ReportFormList",
    mixins: [
		showConfirm,
		withBasicQueries,
		getUserPermissions(reportFormPermissions),
		detectLeavePage("isCreateReportFormDirty")
    ],
    components: {
        ReportFormConfig,
        ReportFormContextMenu
    },
    data() {
        return {
            reportFormData: { ...initReportFormData },
			isShowCreateReportForm: false,
			isCreateReportFormDirty: false
        };
    },
    computed: {
		...mapState("reportFormManagement", [
			"limitPerPage",
			"reportForms",
			"total"
		]),
		totalPages() {
			return Math.ceil(this.total / this.limitPerPage);
		}
	},
	watch: {
		currentPage() {
			this.getReportForms();
		},
		"$route.query"(newVal, oldVal) {
			const newValClone = Object.assign({}, newVal);
			const oldValClone = Object.assign({}, oldVal);
			if (!newValClone.id) {
				delete oldValClone.id;
			}
			if (!_.isEqual(newValClone, oldValClone)) {
				this.handleFetchDataByQuery();
			}
		}
	},
	filters: {
		filterStatus(enable) {
			if (enable) {
				return "Available";
			}

			if (!enable) {
				return "NotAvailable";
			}
		}
	},
    methods: {
        async getReportForms() {
			try {
				this.$vs.loading();

				let queryOptions = {
					orderBy: this.orderBy,
					page: this.currentPage,
					limit: this.limitPerPage
				};

				if (this.searchText) {
					queryOptions.search = this.searchText;
				}

				await this.$store.dispatch("reportFormManagement/getReportForms", queryOptions);
				if (this.$refs.table && this.searchText) {
					this.$refs.table.currentx = 1;
				}
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
        handleClickAddNew() {
            this.isShowCreateReportForm = true;
		},
		handleClickBackCreateReportForm() {
			this.isShowCreateReportForm = false;
			this.isCreateReportFormDirty = false;
			this.reportFormData = { ...initReportFormData };
		},
        async handleCreateReportForm() {
			try {
				this.$vs.loading();
            
				const newReportForm = await this.$store.dispatch("reportFormManagement/addReportForm", {
					...this.reportFormData,
					enable: !this.reportFormData.isDisabled
				});

				this.isCreateReportFormDirty = false;
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("CreateReportFormSuccessful")
					}
				});
				if (newReportForm) {
					this.handleNavigateToConfigPage(newReportForm.id);
				}
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
        },
		handleFetchDataByQuery() {
			this.getReportForms();
		},
		handleNavigateToConfigPage(id) {
			this.$router.push({
				name: this.$constant.PAGES_NAME.PAGE_CBS_REPORT_FORM_CONFIG_NAME,
				params: { id }
			});
		},
		showDeleteConfirm(reportForm) {
			this.mixinShowConfirm({
				text: this.$t("Notify.DeleteConfirmWithName", { name: reportForm.name || "" })
			}).then(async isAccept => {
				if(isAccept) {
					try {
						this.$vs.loading();
						await this.$store.dispatch("reportFormManagement/deleteReportForm", reportForm.id);
						this.$helper.notifySuccess({
							context: this,
							options: {
								text: this.$t("DeleteReportFormSuccessful")
							}
						});
					} catch (error) {
						this.$helper.notifyError({ context: this, error });
					} finally {
						this.$nextTick(() => this.$vs.loading.close());
					}
				}
			});
		}
    },
    created() {
        this.$store.registerModule("reportFormManagement", moduleReportForm);
        this.handleFetchDataByQuery();
    },
    beforeDestroy() {
        this.$store.commit("reportFormManagement/RESET_STATE");
        this.$store.unregisterModule("reportFormManagement");
    }
}
</script>

<style>

</style>